import axios from 'axios';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  fetchQuestionnaire,
  getQuestionnaireScore,
  putQuestionnaire,
  submitQuestionnaire,
} from '@/apis';
import GeneratingResultView from '@/components/GeneratingResultView';
import { Path, QuestionModule } from '@/enums';
import { useApiHandler, useApiRequest } from '@/hooks';
import {
  Questionnaire as QuestionnaireType,
  QuestionWithAnswer,
} from '@/types';

import BusinessModule from './BusinessModule/BusinessModule';
import CommonModule from './CommonModule/CommonModule';
import SDGsModule from './SDGsModule/SDGsModule';

const Questionnaire = () => {
  const [currentModule, setCurrentModule] = useState<string | null>(null);
  const [currentModuleQuestions, setCurrentModuleQuestions] = useState<
    QuestionWithAnswer[]
  >([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionnaireData, setQuestionnaireData] =
    useState<QuestionnaireType | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();

  const { handleApiCall } = useApiHandler();

  const handleCrossModuleNavigation = async (direction: 'prev' | 'next') => {
    const latestData = await handleApiCall(
      () => fetchQuestionnaire(questionnaireId!),
      { showLoading: false },
    );

    if (!latestData?.moduleQuestions) return;

    setQuestionnaireData(latestData);

    const sortedModules = [...latestData.moduleQuestions].sort(
      (a, b) => a.module.order - b.module.order,
    );

    const currentModuleIndex = sortedModules.findIndex(
      (group) => group.module.name === currentModule,
    );

    let targetQuestion: QuestionWithAnswer | null = null;

    switch (direction) {
      case 'prev': {
        if (currentModuleIndex > 0) {
          const prevModule = sortedModules[currentModuleIndex - 1];
          const prevModuleQuestions = prevModule?.questions;
          if (prevModuleQuestions && prevModuleQuestions.length > 0) {
            targetQuestion =
              prevModuleQuestions[prevModuleQuestions.length - 1] || null;
          }
        }
        break;
      }

      case 'next': {
        if (currentModuleIndex < sortedModules.length - 1) {
          const nextModule = sortedModules[currentModuleIndex + 1];
          const nextModuleQuestions = nextModule?.questions;
          if (nextModuleQuestions && nextModuleQuestions.length > 0) {
            targetQuestion = nextModuleQuestions[0] || null;
          }
        } else {
          try {
            await handleApiCall(() => submitQuestionnaire(questionnaireId!), {
              showLoading: true,
            });

            const scoreResult = await handleApiCall(
              () => getQuestionnaireScore(questionnaireId!),
              { showLoading: true },
            );

            if (scoreResult) {
              setIsGenerating(true);
              return;
            }
          } catch (error) {
            console.error(
              'Failed to complete questionnaire or calculate scores:',
              error,
            );
          }
          return;
        }
        break;
      }

      default:
        console.warn('Invalid navigation direction:', direction);
        return;
    }

    if (targetQuestion) {
      await handleApiCall(
        () =>
          putQuestionnaire(
            {
              questionnaireId: questionnaireId!,
              currentQuestionId: targetQuestion.id,
            },
            false,
          ),
        { showLoading: false },
      );

      const targetModuleGroup = latestData.moduleQuestions.find((group) =>
        group.questions.some((q) => q.id === targetQuestion.id),
      );

      if (targetModuleGroup) {
        const targetLocalIndex = targetModuleGroup.questions.findIndex(
          (q) => q.id === targetQuestion.id,
        );

        setCurrentModule(targetModuleGroup.module.name);
        setCurrentModuleQuestions(targetModuleGroup.questions);
        setCurrentQuestionIndex(targetLocalIndex);
      }
    }
  };

  // Use useApiRequest for data fetching with auto-cancellation on unmount
  useApiRequest(() => fetchQuestionnaire(questionnaireId!), {
    immediate: !!questionnaireId,
    requestKey: `questionnaire-${questionnaireId}`,
    onError: (error) => {
      // Redirect to questionnaire selection if user doesn't have permission
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        navigate(Path.QuestionnaireSelection, { replace: true });
      }
    },
    onSuccess: (response) => {
      const data = response.data;

      setQuestionnaireData(data);

      let targetModule: string = QuestionModule.Common;
      let moduleQuestions: QuestionWithAnswer[] = [];

      if (data.currentQuestionId && data.moduleQuestions?.length > 0) {
        const targetModuleGroup = data.moduleQuestions.find((group) =>
          group.questions.some((q) => q.id === data.currentQuestionId),
        );

        if (targetModuleGroup) {
          targetModule = targetModuleGroup.module.name;
          moduleQuestions = targetModuleGroup.questions;
        } else {
          const sortedModules = [...data.moduleQuestions].sort(
            (a, b) => a.module.order - b.module.order,
          );
          targetModule = sortedModules[0]?.module.name || QuestionModule.Common;
          moduleQuestions = sortedModules[0]?.questions || [];
        }
      } else if (data.moduleQuestions?.length > 0) {
        const sortedModules = [...data.moduleQuestions].sort(
          (a, b) => a.module.order - b.module.order,
        );
        targetModule = sortedModules[0]?.module.name || QuestionModule.Common;
        moduleQuestions = sortedModules[0]?.questions || [];
      }

      let currentQuestionIndex = 0;
      if (data.currentQuestionId && moduleQuestions.length > 0) {
        const questionIndex = moduleQuestions.findIndex(
          (q) => q.id === data.currentQuestionId,
        );
        if (questionIndex >= 0) {
          currentQuestionIndex = questionIndex;
        }
      }

      setCurrentModule(targetModule);
      setCurrentModuleQuestions(moduleQuestions);
      setCurrentQuestionIndex(currentQuestionIndex);
      setMounted(true);
    },
  });

  const renderModuleComponent = () => {
    if (!currentModule || !mounted) return null;

    const commonProps = {
      questions: currentModuleQuestions,
      currentQuestionIndex,
      onCrossModuleNavigation: handleCrossModuleNavigation,
    };

    const hasSdgsModule =
      questionnaireData?.moduleQuestions?.some(
        (moduleGroup) => moduleGroup.module.name === QuestionModule.SDGs,
      ) || false;
    const canChangeIndustry = !hasSdgsModule;

    switch (currentModule) {
      case QuestionModule.Common:
        return <CommonModule {...commonProps} />;
      case QuestionModule.Business:
        return (
          <BusinessModule
            {...commonProps}
            canChangeIndustry={canChangeIndustry}
          />
        );
      case QuestionModule.SDGs:
        return (
          <SDGsModule
            {...commonProps}
            industry={questionnaireData?.industry?.name}
          />
        );
      default:
        return null;
    }
  };

  // Show generating result view if in generation state
  if (isGenerating && questionnaireId) {
    return (
      <GeneratingResultView
        questionnaireId={questionnaireId}
        onComplete={() => {
          // Navigate to result page when generation is complete
          navigate(`${Path.Result.replace(':id', questionnaireId)}`);
        }}
        onCancel={() => {
          // Go back to questionnaire selection on cancel
          navigate(Path.QuestionnaireSelection);
        }}
      />
    );
  }

  return renderModuleComponent();
};

export default Questionnaire;
